import { combineReducers, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { IApplicationsState } from 'common/actions/integrations/Applications/interfaces'
import { alert } from 'common/core'
import { getCurrentAccountID } from 'common/core/selectors/appSelectors'
import { ApplicationsApi } from 'shared/api/requests/applications'
import {
  AppSettings,
  GroupAppSettings,
  UpdateSettingsRequest,
} from 'shared/api/requests/applications/schemas'
import { createAsyncAction } from 'shared/lib/redux'

type State = IApplicationsState['settings']

export const updateSettings = createAsyncAction(
  'application/page/settings/update',
  async (
    {
      applicationId,
      settings,
      groupSettings,
    }: {
      applicationId: number
      settings: UpdateSettingsRequest['settings']
      groupSettings: UpdateSettingsRequest['group_settings']
    },
    { getState },
  ) => {
    const accountId = getCurrentAccountID(getState()).replace('fb', '')
    await ApplicationsApi.updateSettings({
      body: {
        app_id: applicationId,
        account_id: Number(accountId),
        settings,
        group_settings: groupSettings,
      },
    })

    alert(l.translate('Settings saved successfully!'), 'success')
  },
  {
    onBusinessError: (error) => {
      error.handle()

      alert(
        l.translate('Something went wrong. Please check settings and try to save again'),
        'danger',
      )
    },
  },
)

const updateSlice = createSlice({
  name: 'application/page/settings/update',
  initialState: { fetching: false, status: 'unknown' },
  reducers: {
    resetUpdateStatus: (state) => {
      state.status = 'unknown'
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateSettings.fulfilled, (state) => {
        state.fetching = false
        state.status = 'success'
      })
      .addCase(updateSettings.rejected, (state) => {
        state.fetching = false
        state.status = 'error'
      })
      .addCase(updateSettings.pending, (state) => {
        state.fetching = true
      })
  },
})

export const { reducer: updateReducer } = updateSlice
export const { resetUpdateStatus } = updateSlice.actions

export const getUpdateFetching = createSelector(
  (state: { applications: { settings: Pick<State, 'update'> } }) =>
    state.applications.settings.update,
  ({ fetching }) => fetching,
)

export const getUpdateStatus = createSelector(
  (state: { applications: { settings: Pick<State, 'update'> } }) =>
    state.applications.settings.update,
  ({ status }) => status,
)

export const fetchSettings = createAsyncAction(
  'application/page/settings/fetch',
  async (applicationId: number, { getState }) => {
    const accountId = getCurrentAccountID(getState()).replace('fb', '')
    const { data } = await ApplicationsApi.fetchSettingsForm({
      query: { account_id: accountId, app_id: applicationId },
    })

    return data
  },
)

type DataState = State['data']

const initialState: DataState = {
  appKey: '',
  fetching: false,
  groupSettings: [] as GroupAppSettings[],
  hasRequest: false,
  settings: [] as AppSettings[],
}

const updateSettingsFieldReducer = (
  state: DataState,
  action: PayloadAction<{ name: string; value: string; groupId?: string }>,
) => {
  const { name, value, groupId } = action.payload
  const settings = groupId
    ? state.groupSettings.find((group) => group.group_id === groupId)?.settings
    : state.settings
  const settingsItem = (settings ?? []).find((item) => item.name === name)
  if (settingsItem) {
    settingsItem.value = value
  }
}

const deleteSettingsGroupReducer = (state: DataState) => {
  if (state.groupSettings.length === 1) {
    return
  }
  const groupIndex = state.groupSettings.findIndex(
    (group) => group.group_id === state.deleteGroupId,
  )
  if (groupIndex !== -1) {
    state.groupSettings.splice(groupIndex, 1)
  }
}

const setDeleteGroupIdReducer = (state: DataState, action: PayloadAction<string>) => {
  state.deleteGroupId = action.payload
}

const clearDeleteGroupIdReducer = (state: DataState) => {
  delete state.deleteGroupId
}

const updateSettingsGroupTitleReducer = (
  state: DataState,
  action: PayloadAction<{ groupId: string; title: string }>,
) => {
  const { groupId, title } = action.payload
  const settingsGroup = state.groupSettings.find((group) => group.group_id === groupId)
  if (settingsGroup) {
    settingsGroup.title = title
  }
}

const createGroupId = () => {
  return new Date().getTime() + '_'
}

const addSettingsGroupReducer = (state: DataState) => {
  const newSettingsGroup = {
    title: `Group ${state.groupSettings.length + 1}`,
    group_id: createGroupId(),
    settings: state.groupSettings[0].settings.map(({ name, title }) => ({
      name,
      title,
      value: '',
    })),
  }

  state.groupSettings.push(newSettingsGroup)
}

const dataSettingsSlice = createSlice({
  name: 'application/page/settings',
  initialState,
  reducers: {
    addSettingsGroup: addSettingsGroupReducer,
    clearDeleteGroupId: clearDeleteGroupIdReducer,
    deleteSettingsGroup: deleteSettingsGroupReducer,
    setDeleteGroupId: setDeleteGroupIdReducer,
    updateSettingsField: updateSettingsFieldReducer,
    updateSettingsGroupTitle: updateSettingsGroupTitleReducer,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.settings = []
        state.groupSettings = []
        state.appKey = ''
        state.hasRequest = false
        state.fetching = true
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.settings = action.payload.settings
        state.groupSettings = (action.payload.group_settings ?? []).map(
          ({ title, group_id, settings }) => ({
            title: title || 'Group 1',
            group_id: group_id ?? createGroupId(),
            settings,
          }),
        )
        state.appKey = action.payload.appKey ?? ''
        state.hasRequest = action.payload.hasRequest
        state.fetching = false
      })
      .addCase(fetchSettings.rejected, (state) => {
        state.fetching = false
      })
  },
})

export const {
  addSettingsGroup,
  clearDeleteGroupId,
  deleteSettingsGroup,
  setDeleteGroupId,
  updateSettingsField,
  updateSettingsGroupTitle,
} = dataSettingsSlice.actions

export const { reducer: dataReducer } = dataSettingsSlice

export const getApplicationSettings = createSelector(
  (state: { applications: { settings: Pick<State, 'data'> } }) => state.applications.settings.data,
  (data) => data,
)

export const reducer = combineReducers({
  data: dataReducer,
  update: updateReducer,
})
